import * as React from 'react';

import { Box } from '../../components/ui/box';
import { Button } from '../../components/ui/button';
import { Icon } from '../../components/ui/icon';
import { Menu, MenuPopup, MenuTrigger } from '../../components/ui/menu';
import {
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import { DataTableRowKebabProvider } from './data-table-context';
import {
  ROW_ACTIONS_END_INSET,
  ROW_FLOATING_CHROME_CLASS,
} from './data-table-utils';

/**
 * The row's "…" menu. It lives in the row's LEADING content cell — the
 * identity column, pinned in every dense view — not in a trailing lane: the
 * identity cell is the one place on screen at every scroll position and every
 * viewport, where a trailing kebab is either a pinned lane that hard-cuts the
 * columns scrolling under it or an unpinned cell that scrolls out of reach.
 *
 * It is a member of the host cell's action cluster — literally: a cell with a
 * hover-revealed cluster (DataTableHoverActionsCell) adopts the kebab as that
 * cluster's LAST item, so the cell's rename/favorite/copy actions and the
 * kebab ride one float, one hover scope and one backing ramp. Two floats on
 * one edge cannot share a fade: each ramps its own 16px lead-in, and the
 * later one's lead-in lands on the earlier one's last button. Only a host
 * cell with no hover cluster gets the standalone float (DataTableRowActions).
 *
 * It wears the cluster's chrome — the opaque outline chip at `icon-sm` with a
 * muted glyph and a top tooltip, the same recipe as a cell's
 * rename/favorite/copy actions — and the row's ambient square-button rule
 * gives it the row's corners.
 *
 * `actions` is the row's `renderRowActions` result; DataTable renders no
 * kebab for a row whose result is null — a per-row opt-out (e.g. a locked
 * built-in row), not a disabled stub.
 */
export function DataTableRowKebab({ actions }: { actions: React.ReactNode }) {
  return (
    <Menu>
      {/* TooltipTrigger inside the menu trigger's render chain (the column
          picker's recipe). */}
      <Tooltip>
        <MenuTrigger
          render={
            <TooltipTrigger
              render={
                // 28px: the in-row action rung. The regular cell ring is
                // rowHeight − 4 = 40px, so the chip stands 6px inside it —
                // a chip that fills the ring reads as the row's chrome
                // instead of an action on it.
                <Button
                  variant="outline"
                  size="icon-sm"
                  aria-label="More options"
                />
              }
            />
          }
        >
          <Icon name="ellipsis" color="muted" />
        </MenuTrigger>
        <TooltipPopup side="top">More options</TooltipPopup>
      </Tooltip>
      {/* Row-action menus are the `action` genre — the same 256px floor
          the row's right-click context menu answers. */}
      <MenuPopup align="start" width="action">
        {actions}
      </MenuPopup>
    </Menu>
  );
}

/**
 * The kebab's STANDALONE float, for a host cell whose content has no hover
 * cluster to adopt it (a plain DataTableCell, an always-visible cluster).
 * DataTable floats it over the trailing edge of the host cell: it reserves no
 * inline width, so at rest the value runs the whole cell and truncates only
 * where the cell ends — ROW_FLOATING_CHROME_CLASS owns the float, the
 * pointer gating and the backing ramp. It reveals on the ROW's hover scope
 * (`group/row`), held visible while a popup anchored anywhere in the row is
 * open, on focus, and on the armed (tapped) row on touch, and sits
 * ROW_ACTIONS_END_INSET from the cell's edge.
 */
export function DataTableRowActions({
  actions,
}: {
  actions: React.ReactNode;
}) {
  return (
    <Box
      // Row chrome: hover on a mouse, the armed (tapped) row on touch.
      hoverReveal="row"
      direction="row"
      align="center"
      right={ROW_ACTIONS_END_INSET}
      className={ROW_FLOATING_CHROME_CLASS}
    >
      <DataTableRowKebab actions={actions} />
    </Box>
  );
}

/**
 * The host cell's wrapper: the cell's own content keeps the WHOLE width, and
 * the row's kebab is OFFERED to it through DataTableRowKebabProvider. A hover
 * cluster inside the content adopts the kebab (announcing it from a layout
 * effect, so nothing paints twice) and this host renders no float of its own;
 * until — or unless — one does, the kebab floats standalone over the
 * content's trailing edge. Either way the kebab reserves no inline width, so
 * the value truncates where the cell ends and not where a hidden chip would
 * sit.
 */
export function DataTableRowKebabHost({
  actions,
  children,
}: {
  actions: React.ReactNode;
  children: React.ReactNode;
}) {
  const [adopted, setAdopted] = React.useState(false);
  const adopt = React.useCallback(() => {
    setAdopted(true);
    return () => setAdopted(false);
  }, []);
  const value = React.useMemo(() => ({ actions, adopt }), [actions, adopt]);

  return (
    <DataTableRowKebabProvider value={value}>
      <Box
        direction="row"
        align="center"
        fill
        position="relative"
        className="min-w-0"
      >
        {children}
        {adopted ? null : <DataTableRowActions actions={actions} />}
      </Box>
    </DataTableRowKebabProvider>
  );
}
